import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Hash, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Quick lookup that lets buyers paste a WakaCode (10 digits) or an agency
 * Reference and jump straight to the property detail page. Designed to live
 * just under the main SearchBar on the homepage and the search page.
 */
export default function QuickCodeLookup({ variant = 'hero' }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);

  const tt = (en, es) => (language === 'es' ? es : en);

  const submit = async (e) => {
    e?.preventDefault();
    const code = value.trim();
    if (!code) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/properties/by-code/${encodeURIComponent(code)}`);
      if (r.data?.id) {
        navigate(`/property/${r.data.id}`);
        setValue('');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        toast.error(tt(`No property matches "${code}"`, `No hay propiedad con "${code}"`));
      } else if (status === 400) {
        toast.error(tt('Invalid code', 'Código inválido'));
      } else {
        toast.error(tt('Lookup failed, try again', 'Error de búsqueda, intenta de nuevo'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Hero variant: dark glass over the navy hero, gold accents.
  if (variant === 'hero') {
    return (
      <form
        onSubmit={submit}
        className="flex items-stretch gap-2 flex-1 min-w-0"
        data-testid="quick-code-lookup-hero"
      >
        <div className="flex-1 min-w-0 flex items-center gap-2 bg-white/8 backdrop-blur-md border border-white/15 rounded-xl px-3 hover:border-[#D4AF37]/40 focus-within:border-[#D4AF37]/60 transition">
          <Hash className="w-4 h-4 text-[#D4AF37] shrink-0" />
          <Input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={tt('Have a code? e.g. 2413000026', '¿Tienes un código? Ej. 2413000026')}
            maxLength={64}
            className="flex-1 min-w-0 h-12 border-0 bg-transparent text-white placeholder:text-white/50 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm font-mono"
            data-testid="quick-code-input"
            autoComplete="off"
            spellCheck="false"
          />
        </div>
        <Button
          type="submit"
          disabled={loading || !value.trim()}
          className="h-12 px-4 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1.5 rounded-xl font-medium"
          data-testid="quick-code-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {tt('Go', 'Ir')}
        </Button>
      </form>
    );
  }

  // Light variant: for use on light backgrounds (search page, etc.)
  return (
    <form
      onSubmit={submit}
      className="flex items-stretch gap-2"
      data-testid="quick-code-lookup-light"
    >
      <div className="flex-1 flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 hover:border-[#D4AF37]/40 focus-within:border-[#D4AF37]/60 transition">
        <Hash className="w-4 h-4 text-[#D4AF37] shrink-0" />
        <Input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={tt(
            'Paste WakaCode or Reference',
            'Pega WakaCode o Referencia'
          )}
          maxLength={64}
          className="flex-1 h-10 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-sm font-mono"
          data-testid="quick-code-input"
          autoComplete="off"
          spellCheck="false"
        />
      </div>
      <Button
        type="submit"
        disabled={loading || !value.trim()}
        className="h-11 px-4 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1.5"
        data-testid="quick-code-submit"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {tt('Go', 'Ir')}
      </Button>
    </form>
  );
}
